import { createSlice, current, PayloadAction } from "@reduxjs/toolkit";
import { useStore } from "react-redux";

import {
  IBoxInstance,
  IBuilding,
  IClient,
  IClientRole,
  IFloor,
  ILayoutItem,
  ILoadingDisable,
  IModuleAccessMap,
  IObjectFavorite,
  IObjectLoading,
  IPrivilege,
  ISite,
  ISubsystem,
  IUser,
} from "../interfaces";
import { IContract } from "../services/endpoints/newAPI/customQueries";

export interface AppSliceState {
  moduleAccessMap: IModuleAccessMap;
  privileges: IPrivilege[];
  privs: any;
  user: IUser;
  users: IUser[];
  clientUsers: IUser[];
  loading: IObjectLoading;
  favourite: IObjectFavorite;
  skip: boolean;
  suppliers: IClient[]
  sites: ISite[];
  clients: IClient[];
  contracts: IContract[];
  buildings: IBuilding[];
  instances: IBoxInstance[];
  roles: IClientRole[];
  floors: IFloor[];
  fcDashboardResolution: {
    width: number;
    height: number;
  };
  fullScreenLayout: ILayoutItem[];
  subsystems: ISubsystem[];
  currentClient?: IClient | null;
  currentBuilding: IBuilding | null;
  currentFloor: IFloor | null;
  currentFloorplan: IContract | null;
}

type AppType = {
  moduleAccessMap: IModuleAccessMap;
  sites: ISite[];
  privileges: IPrivilege[];
  privs: any;
  user: IUser | undefined;
  users: IUser[];
  clientUsers: IUser[];
  loading: IObjectLoading;
  favourite: IObjectFavorite;
  skip: boolean;
  subsystems: ISubsystem[];
  suppliers: IClient[];
  clients: IClient[];
  contracts: IContract[];
  dashboards: [];
  buildings: IBuilding[];
  instances: IBoxInstance[];
  roles: IClientRole[];
  floors: IFloor[];
  fullScreenLayout: ILayoutItem[];
  fcDashboardResolution: {
    width: number;
    height: number;
  };
  currentClient?: IClient | null;
  currentBuilding: IBuilding | null;
  currentFloor: IFloor | null;
  currentFloorplan: IContract | null;
};

const initialState: AppType = {
  moduleAccessMap: {},
  sites: [],
  privileges: [],
  privs: {},
  user: undefined,
  subsystems: [],
  favourite: {
    site: "0",
    client: "0",
    contract: "0",
    building: "0",
    floor: "0",
    floorplan: "0",
    lastActiveTime: null,
    lastActiveInstanceTime: null,
  },
  loading: {
    clients: true,
    contracts: true,
    buildings: true,
    subsystems: true,
    roles: true,
    privileges: true,
    users: true,
    clientUsers: true,
  },
  fullScreenLayout: [],
  skip: true,
  clients: [],
  suppliers: [],
  contracts: [],
  clientUsers: [],
  users: [],
  dashboards: [],
  buildings: [],
  floors: [],
  roles: [],
  fcDashboardResolution: {
    width: 0,
    height: 0,
  },
  currentClient: null,
  currentBuilding: null,
  currentFloor: null,
  currentFloorplan: null,
  instances: [],
};

export const apiDataSlice = createSlice({
  name: "apiData",
  initialState,
  reducers: {
    apiLogout: () => initialState,
    setPrivileges(
      state: AppSliceState,
      action: PayloadAction<{ privileges: IPrivilege[] }>
    ) {
      state.privileges = action.payload.privileges;

      //Generate structural equivalent of above:
      const pmap = {};
      action?.payload?.privileges?.forEach((p) => {
        const ps = String(p?.name).split(".");

        var pp = pmap;
        var key;
        while ((key = ps.shift())) {
          if (!pp[key]) {
            pp[key] = {};
          }
          pp = pp[key];
        }
      });
      state.privs = pmap;
    },

    setFullscreenLayout(
      state: AppSliceState,
      action: PayloadAction<{ layout: ILayoutItem[] }>
    ) {
      state.fullScreenLayout = action.payload.layout;
    },
    disableLoading(
      state: AppSliceState,
      action: PayloadAction<{ type: ILoadingDisable }>
    ) {
      const { type } = action.payload;
      switch (type) {
        case "clients": {
          state.loading.clients = false;
          break;
        }
        case "contracts": {
          state.loading.contracts = false;
          break;
        }
        case "buildings": {
          state.loading.buildings = false;
          break;
        }
        case "subsystems": {
          state.loading.subsystems = false;
          break;
        }
        case "roles": {
          state.loading.roles = false;
          break;
        }
        case "privileges": {
          state.loading.privileges = false;
          break;
        }
        case "users": {
          state.loading.users = false;
          break;
        }
        case "clientUsers": {
          state.loading.clientUsers = false;
          break;
        }
      }
    },
    setFavourite(
      state: AppSliceState,
      action: PayloadAction<{ type: string; value: string }>
    ) {
      const { type, value } = action.payload;

      switch (type) {
        case "client": {
          state.favourite.client = value;
          // Auto-update currentClient when favourite client changes
          state.currentClient =
            state.clients.find((c) => c.id === value) || undefined;
          if (typeof window !== "undefined") {
            try {
              window.localStorage.setItem("client", value);
            } catch {}
          }
          break;
        }
        case "contract": {
          state.favourite.contract = value;
          break;
        }
        case "building": {
          state.favourite.building = value;

          state.currentBuilding =
            state.buildings.find((b) => b.id === value) || null;

          break;
        }
        case "floor": {
          state.favourite.floor = value;
          break;
        }
        case "floorplan": {
          state.favourite.floorplan = value;
          break;
        }
        case "site": {
          state.favourite.site = value;
          break;
        }
      }

      if (window.sessionStorage) {
        window.sessionStorage.setItem(
          "hub2-favs",
          JSON.stringify(state.favourite)
        );
      }
    },
    setSkip(state: AppSliceState, action: PayloadAction<{ skip: boolean }>) {
      state.skip = action.payload.skip;
    },
    setSubsystems(
      state: AppSliceState,
      action: PayloadAction<{ subsystems: ISubsystem[] }>
    ) {
      state.subsystems = action.payload.subsystems;
    },
    setFcDashboardResolution(
      state: AppSliceState,
      action: PayloadAction<{ res: { width: number; height: number } }>
    ) {
      state.fcDashboardResolution.width = action.payload.res.width;
      state.fcDashboardResolution.height = action.payload.res.height;
    },

    setClients(
      state: AppSliceState,
      action: PayloadAction<{ data: IClient[] }>
    ) {
      state.suppliers = action.payload.data.filter((c) => c.isSupplier === "1");
      state.clients = action.payload.data.filter((c) => {
        if (+c.isSupplier === 1 && +c.isContracted === 0) {
          return false;
        }
        return true;
      });

      // Auto-update currentClient when clients are loaded
      if (state.favourite.client !== "0") {
        state.currentClient =
          state.clients.find((c) => c.id === state.favourite.client) ||
          undefined;
      }
    },
    setClientUsers(
      state: AppSliceState,
      action: PayloadAction<{ data: IUser[] }>
    ) {
      state.clientUsers = action.payload.data;
    },
    setUsers(
      state: AppSliceState,
      action: PayloadAction<{ data: IUser[] }>
    ) {
      state.users = action.payload.data;
    },
    setContracts(
      state: AppSliceState,
      action: PayloadAction<{ data: IContract[] }>
    ) {
      state.contracts = action.payload.data;
    },
    setModuleAccessMap(
      state: AppSliceState,
      action: PayloadAction<{ moduleAccessMap: IModuleAccessMap }>
    ) {
      state.moduleAccessMap = action.payload.moduleAccessMap;
    },
    setSites(state: AppSliceState, action: PayloadAction<{ sites: ISite[] }>) {
      state.sites = action.payload.sites;
    },
    setBuildings(
      state: AppSliceState,
      action: PayloadAction<{ data: IBuilding[] }>
    ) {
      state.buildings = action.payload.data;
    }, 
    setBoxInstances(
      state: AppSliceState,
      action: PayloadAction<{ data: IBoxInstance[] }>
    ) {
      state.instances = action.payload.data;
    },
    setFloors(
      state: AppSliceState,
      action: PayloadAction<{ data: IFloor[] }>
    ) {
      state.floors = action.payload.data;
    },
    setRoles(
      state: AppSliceState,
      action: PayloadAction<{ data: IClientRole[] }>
    ) {
      state.roles = action.payload.data;
    },
    setLastActiveTime(
      state: AppSliceState,
      action: PayloadAction<{ lastActiveTime: Date }>
    ) {
      state.favourite.lastActiveTime = action.payload.lastActiveTime;
    },
    setLastActiveInstanceTime(
      state: AppSliceState,
      action: PayloadAction<{ lastActiveInstanceTime: Date }>
    ) {
      state.favourite.lastActiveInstanceTime = action.payload.lastActiveInstanceTime;
    },
  },
});

export const {
  apiLogout,
  setSkip,
  setContracts,
  setBuildings,
  setFcDashboardResolution,
  setFullscreenLayout,
  setFloors,
  setClients,
  setRoles,
  disableLoading,
  setFavourite,
  setSubsystems,
  setPrivileges,
  setSites,
  setUsers,
  setClientUsers,
  setModuleAccessMap,
  setLastActiveTime,
  setBoxInstances,
  setLastActiveInstanceTime
} = apiDataSlice.actions;

export default apiDataSlice.reducer;
